import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
const ACTION_BADGE = {
  edit: { bg: "#FEF3C7", fg: "#92400E", label: "EDIT" },
  confirm: { bg: "#DCFCE7", fg: "#166534", label: "CONFIRM" },
  reject: { bg: "#FEE2E2", fg: "#991B1B", label: "REJECT" },
  delete: { bg: "#FEE2E2", fg: "#991B1B", label: "DELETE" },
};
const ACTIONS = ["", "edit", "confirm", "reject"];

export default function IntakeAudit() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [open, setOpen] = useState({}); // id -> expanded

  const load = () => {
    setLoading(true);
    api.get("/intake/audit", { params: { action: action || undefined, actor: actor.trim() || undefined } })
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Failed to load audit log"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [action]);

  return (
    <Layout breadcrumb={["Home", "PO Intake Review", "Audit Log"]}>
      <div className="jde-page">
        <div className="jde-panel" data-testid="intake-audit-panel">
          <div className="jde-panel-header">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="jde-btn jde-btn-sm" onClick={() => navigate("/intake-review")} data-testid="audit-back-review"
                      title="Back to PO Intake Review">&larr; Intake Review</button>
              <div className="jde-panel-title" style={{ margin: 0 }}>PO INTAKE AUDIT LOG ({rows.length}{loading ? "…" : ""})</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ display: "flex", gap: 6 }}>
                {["", "edit", "confirm", "reject"].map((a) => (
                  <button key={a || "all"} className={`jde-btn jde-btn-sm ${action === a ? "jde-btn-primary" : ""}`}
                          onClick={() => setAction(a)} data-testid={`audit-filter-${a || "all"}`}>
                    {a ? a.toUpperCase() : "ALL"}
                  </button>
                ))}
              </div>
              <input className="jde-input" placeholder="Filter by user…" value={actor}
                     onChange={(e) => setActor(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && load()}
                     data-testid="audit-actor-filter" style={{ width: 160 }} />
              <button className="jde-btn jde-btn-sm" onClick={load} data-testid="audit-refresh">Search</button>
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <table className="jde-grid" style={{ width: "100%" }} data-testid="intake-audit-table">
              <thead>
                <tr>
                  <th style={{ width: 34 }}></th>
                  <th>When</th>
                  <th>Action</th>
                  <th>PO #</th>
                  <th>Customer</th>
                  <th>User</th>
                  <th style={{ textAlign: "right" }}>Changes</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const b = ACTION_BADGE[a.action] || { bg: "#E2E8F0", fg: "#334155", label: (a.action || "").toUpperCase() };
                  const nChanges = (a.changes || []).length;
                  const isOpen = !!open[a.id];
                  return (
                    <Fragment key={a.id}>
                      <tr data-testid={`audit-row-${a.id}`}
                          onClick={() => nChanges && setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
                          style={{ cursor: nChanges ? "pointer" : "default" }}>
                        <td style={{ textAlign: "center", color: "var(--omh-text-muted)" }}>
                          {nChanges ? (isOpen ? "▾" : "▸") : ""}
                        </td>
                        <td className="text-mono" style={{ fontSize: 11 }}>{fmtDateTime(a.timestamp) || a.timestamp}</td>
                        <td><span className="jde-badge" style={{ background: b.bg, color: b.fg, fontWeight: 700, fontSize: 10 }}>{b.label}</span></td>
                        <td className="text-mono" style={{ fontWeight: 700 }}>{a.po_number || "—"}</td>
                        <td>{a.customer_name || "—"}</td>
                        <td>{a.actor || "system"}{a.actor_role ? <span className="muted" style={{ fontSize: 10.5 }}> ({a.actor_role})</span> : null}</td>
                        <td className="num text-mono">{nChanges || "—"}</td>
                        <td style={{ color: a.note ? "var(--omh-alert)" : "inherit" }}>{a.note || "—"}</td>
                      </tr>
                      {isOpen && nChanges > 0 && (
                        <tr key={`${a.id}-detail`} data-testid={`audit-detail-${a.id}`}>
                          <td></td>
                          <td colSpan={7} style={{ background: "#F8FAFC" }}>
                            <table className="jde-grid" style={{ width: "100%" }}>
                              <thead><tr><th style={{ fontSize: 10 }}>Field</th><th style={{ fontSize: 10 }}>From</th><th style={{ fontSize: 10 }}>To</th></tr></thead>
                              <tbody>
                                {a.changes.map((c, i) => (
                                  <tr key={i}>
                                    <td style={{ fontSize: 11 }}>{c.field}</td>
                                    <td style={{ fontSize: 11, color: "#94A3B8", textDecoration: "line-through" }}>{c.old ?? "—"}</td>
                                    <td style={{ fontSize: 11, fontWeight: 700 }}>{c.new ?? "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={8} className="muted" style={{ textAlign: "center", padding: 20 }}>No audit activity recorded.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
